import React, { useEffect, useRef, useCallback } from 'react';

export const CustomCursor = () => {
  const cursorRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const rafId = useRef<number>(0);
  const currentState = useRef<string>('hidden');
  const targetX = useRef(0);
  const targetY = useRef(0);

  // Don't render on touch devices (no mouse cursor to augment)
  const isTouchDevice = typeof window !== 'undefined' && !window.matchMedia('(pointer: fine)').matches;
  
  const updateCursorState = useCallback((target: HTMLElement) => {
    if (isTouchDevice) return;
    let newState = 'hidden';
    if (target.closest('.cursor-draggable') || (target.closest('[role="button"]') && target.closest('[aria-roledescription="sortable"]'))) {
      newState = 'drag';
    } else if (target.closest('.cursor-view-project')) {
      newState = 'view';
    } else if (target.closest('a[href]') && (target.closest('a[target="_blank"]') || target.closest('a[href^="http"]'))) {
      newState = 'open';
    }

    if (newState !== currentState.current) {
      currentState.current = newState;
      if (cursorRef.current && labelRef.current) {
        const labels: Record<string, string> = { drag: 'DRAG', view: 'VIEW', open: 'OPEN' };
        if (newState === 'hidden') {
          cursorRef.current.style.opacity = '0';
          cursorRef.current.style.transform = 'scale(0.8)';
        } else {
          labelRef.current.textContent = labels[newState] || '';
          cursorRef.current.style.opacity = '1';
          cursorRef.current.style.transform = 'scale(1)';
        }
      }
    }
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      targetX.current = e.clientX;
      targetY.current = e.clientY;
      updateCursorState(e.target as HTMLElement);

      if (!rafId.current) {
        rafId.current = requestAnimationFrame(() => {
          if (cursorRef.current) {
            cursorRef.current.style.left = `${targetX.current + 15}px`;
            cursorRef.current.style.top = `${targetY.current + 15}px`;
          }
          rafId.current = 0;
        });
      }
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [updateCursorState]);

  if (typeof window === 'undefined' || isTouchDevice) return null;

  return (
    <div
      ref={cursorRef}
      className="fixed pointer-events-none z-[9999] bg-white text-black text-[10px] font-bold px-3 py-1.5 tracking-widest rounded-full shadow-lg flex items-center justify-center min-w-[60px]"
      style={{
        opacity: 0,
        transition: 'opacity 0.15s, transform 0.15s',
        willChange: 'left, top',
      }}
    >
      <span ref={labelRef} />
    </div>
  );
};
